import { useCallback, useEffect, useState } from "preact/hooks";

import {
  archiveProject as archiveProjectApi,
  createProject as createProjectApi,
  fetchProjectBids,
  fetchProjects,
  patchProject as apiPatchProject,
  restoreProject as restoreProjectApi,
  type CreateProjectBody,
  type PatchProjectBody,
} from "../lib/api";
import type { BidOut, ProjectOut } from "../types";

/**
 * Sprint-005 C4 — the More tab's Projects list. `GET /projects` is
 * admin/manager/api (`app/api/v1/projects.py`) — a member gets a 403
 * (ADR-002 §5/§11 grants no member-level "view projects" cell at all).
 * `enabled` here IS a real role gate (unlike `useAnimals`'s) — the caller
 * must pass `canHandOutWork` (admin OR manager), matching the server's own
 * `_ADMIN_MANAGER_API_READ` scope exactly, so a member never even issues
 * this request.
 *
 * Sprint-013 C2 (design-spec-animal-cards-2026-07-10.md §5, ux-ruling-
 * inactive-grid-2026-07-10.md — E1 PM ruling precedent, mirrored) —
 * `refresh()` always fetches WITH archived rows included
 * (`fetchProjects(true)`), so `ProjectsView`'s Active/All toggle can filter
 * client-side with no second request — the exact same "fetch everything
 * once, filter locally" shape `useAnimals`/`AnimalsView`'s toggle already
 * established. Also adds the Add/Edit/Archive/Restore CRUD flow.
 */
export function useProjects(enabled: boolean) {
  const [projects, setProjects] = useState<ProjectOut[] | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setError(null);
    try {
      const rows = await fetchProjects(true);
      setProjects(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load the projects list.");
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Sprint-010 C4 (design-phase-c.md §3.1, ADR-002 A6 decision 7.1/7.4),
  // widened sprint-013 C2 to also cover `name` (SA ruling Decision 2.5) —
  // the project detail's Edit sheet submit, admin/api only. Patches the
  // ONE changed row directly into local state (the full `updated` row —
  // `ProjectPatchOut` already carries every `ProjectOut` field) — no
  // `refresh()` call, same §8 TD-010-class rule `useAnimals.
  // updateAnimalFields` already follows: `ProjectDetailOverlay` (already
  // open, reading this same array via `.find()`) never remounts/flashes.
  const patchProject = useCallback(
    async (projectId: string, updates: Pick<PatchProjectBody, "name" | "summary" | "status">) => {
      const updated = await apiPatchProject(projectId, {
        idempotency_key: crypto.randomUUID(),
        ...updates,
      });
      setProjects((prev) => (prev ? prev.map((p) => (p.id === projectId ? updated : p)) : prev));
      return updated;
    },
    [],
  );

  // Sprint-013 C2 (design-spec-animal-cards-2026-07-10.md §5) — the
  // "+ Add" sheet's submit. Appends the created row directly into local
  // state (the same §8 TD-010-class rule as `patchProject` above) — no
  // `refresh()` call, so the list never remounts/flashes on add.
  const createProject = useCallback(async (input: Omit<CreateProjectBody, "idempotency_key">) => {
    const created = await createProjectApi({ idempotency_key: crypto.randomUUID(), ...input });
    setProjects((prev) => (prev ? [...prev, created] : [created]));
    return created;
  }, []);

  // Sprint-013 C2 (design-spec-animal-cards-2026-07-10.md §5, SA ruling
  // Decision 2.4) — the Archive flow's `DELETE /projects/{id}`. Unlike
  // `archiveAnimal` (a `PATCH .../active` reusing the full row response),
  // this endpoint's response (`ProjectArchiveOut`) only carries
  // `id`/`archived_at`/`replayed` — patch just that one field into local
  // state, in place, same §8 TD-010-class rule.
  const archiveProject = useCallback(async (projectId: string) => {
    const result = await archiveProjectApi(projectId, crypto.randomUUID());
    setProjects((prev) =>
      prev
        ? prev.map((p) => (p.id === projectId ? { ...p, archived_at: result.archived_at } : p))
        : prev,
    );
    return result;
  }, []);

  // Sprint-013 C2 (design-spec-animal-cards-2026-07-10.md §5, SA ruling
  // Decision 2.4) — the Restore flow's `POST /projects/{id}/restore`.
  // `ProjectRestoreOut` carries the full row (like `patchProject`'s
  // response) — patch it in place wholesale.
  const restoreProject = useCallback(async (projectId: string) => {
    const updated = await restoreProjectApi(projectId, crypto.randomUUID());
    setProjects((prev) => (prev ? prev.map((p) => (p.id === projectId ? updated : p)) : prev));
    return updated;
  }, []);

  return {
    projects,
    loading,
    error,
    refresh,
    patchProject,
    createProject,
    archiveProject,
    restoreProject,
  };
}

/**
 * A project's quotes (bids) — admin/api ONLY server-side
 * (`GET /projects/{id}/bids`), denied to manager AND member alike
 * (ADR-002 §11). `enabled` must be `isAdmin` specifically, not
 * `canHandOutWork` — passing anything looser would issue a request a
 * manager's session gets a 403 for, which is harmless correctness-wise but
 * wrong by this sprint's own brief ("role-gated render AND no data
 * fetch"): the fetch itself must never fire for a non-admin, not just have
 * its result hidden.
 */
export function useProjectBids(projectId: string | null, enabled: boolean) {
  const [bids, setBids] = useState<BidOut[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!projectId || !enabled) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchProjectBids(projectId);
      setBids(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load the quotes.");
    } finally {
      setLoading(false);
    }
  }, [projectId, enabled]);

  useEffect(() => {
    setBids(null);
    if (!enabled) return;
    void refresh();
  }, [refresh, enabled]);

  return { bids, loading, error, refresh };
}
